'use client';

import { useState, useEffect, useCallback } from 'react';
import { clientKycApi } from '../api';
import type { ClientKYCStatusResponse, KYCDocument, DocumentType } from '../types';

interface UseKycReturn {
  kycStatus: ClientKYCStatusResponse | null;
  documents: KYCDocument[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  uploadDocument: (
    file: File,
    documentType: DocumentType,
    onProgress?: (progress: number) => void
  ) => Promise<void>;
  deleteDocument: (documentId: string) => Promise<void>;
}

export function useKyc(clientId: string, tenantSlug: string): UseKycReturn {
  const [kycStatus, setKycStatus] = useState<ClientKYCStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchKycStatus = useCallback(async () => {
    if (!clientId || !tenantSlug) return;
    
    try {
      setIsLoading(true);
      setError(null);
      const result = await clientKycApi.getKYCStatus(clientId, tenantSlug);
      setKycStatus(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch KYC status'));
    } finally {
      setIsLoading(false);
    }
  }, [clientId, tenantSlug]);

  useEffect(() => {
    fetchKycStatus();
  }, [fetchKycStatus]);

  const uploadDocument = useCallback(
    async (file: File, documentType: DocumentType, onProgress?: (progress: number) => void) => {
      try {
        await clientKycApi.uploadDocument(file, tenantSlug, clientId, documentType, onProgress);
        await fetchKycStatus(); // Refresh after upload
      } catch (err) {
        throw err instanceof Error ? err : new Error('Failed to upload document');
      }
    },
    [clientId, tenantSlug, fetchKycStatus]
  );

  const deleteDocument = useCallback(
    async (documentId: string) => {
      try {
        await clientKycApi.deleteDocument(documentId, tenantSlug, clientId);
        await fetchKycStatus(); // Refresh after delete
      } catch (err) {
        throw err instanceof Error ? err : new Error('Failed to delete document');
      }
    },
    [clientId, tenantSlug, fetchKycStatus]
  );

  return {
    kycStatus,
    documents: kycStatus?.documents || [],
    isLoading,
    error,
    refetch: fetchKycStatus,
    uploadDocument,
    deleteDocument,
  };
}
